import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    User, Phone, MapPin, Wallet, CreditCard, FileText, Clock, Activity, Gift, Package,
    ArrowLeft, Loader2, IndianRupee, Edit, Plus, Users, Layers, Trash2, Crown, TrendingUp,
    History, Heart, Tag, MessageCircle, CheckCircle2,
} from 'lucide-react';
import styles from './ClientProfile.module.css';
import { useStore } from '../../../context/StoreContext';
import { useAuth } from '../../../context/AuthContext';
import { SMIIFY_API } from '../../../config/api';
import EditClientDrawer from './EditClientDrawer/EditClientDrawer';

const CUSTOMER_API = `${SMIIFY_API}/customers`;

const money = (val) => Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A');
const formatTime = (d) => (d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '');
const maskPhone = (p) => { const s = String(p || ''); return s.length >= 6 ? `${s.slice(0, 2)}${'•'.repeat(s.length - 4)}${s.slice(-2)}` : '••••'; };

const isExpired = (expiryDate) => (expiryDate ? new Date(expiryDate) < new Date() : false);
const getDaysLeft = (expiryDate) => (expiryDate ? Math.ceil((new Date(expiryDate) - new Date()) / 86400000) : Infinity);
const GENDER_LABEL = { Male: 'Male', Female: 'Female', Other: 'Other' };
const EMPTY_INSIGHTS = { preferredStaff: null, staffBreakdown: [], favouriteServices: [], totalBills: 0, totalServices: 0 };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const ClientProfile = () => {
    const { id: customerId } = useParams();
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const { hasPermission } = useAuth();
    const storeId = activeStore?._id || activeStore?.id;
    const jwtToken = localStorage.getItem('smiify_token');

    // Live access (the server checks the same rules)
    const canEdit = hasPermission('customers', 'edit');
    const canSeePhone = hasPermission('customers', 'view_phone');
    const canSeeSpend = hasPermission('customers', 'view_spend');
    const canSeeHistory = hasPermission('customers', 'view_history');
    const canEditPhone = hasPermission('customers', 'edit_phone');
    const phoneText = (p) => (canSeePhone ? p : maskPhone(p));

    const [activeTab, setActiveTab] = useState('overview');
    const [isLoading, setIsLoading] = useState(true);
    const [customer, setCustomer] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [serviceHistory, setServiceHistory] = useState([]);
    const [insights, setInsights] = useState(EMPTY_INSIGHTS);

    // edit drawer: null = closed, '' = open at the top, 'family' = open at the family section
    const [editFocus, setEditFocus] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [toast, setToast] = useState('');
    const [changes, setChanges] = useState(null);       // profile change history, loaded when its tab opens

    const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}`, 'x-store-id': storeId }), [jwtToken, storeId]);

    const fetchCustomerData = useCallback(async () => {
        if (!customerId || !storeId || storeId === 'global_hq' || !jwtToken) { setIsLoading(false); return; }
        setIsLoading(true);
        try {
            const custRes = await fetch(`${CUSTOMER_API}/${customerId}`, { headers });
            const custData = await custRes.json().catch(() => ({}));
            if (custData.success) setCustomer({ ...custData.data, family: custData.data.family || [] });

            // Only this client's records (the server filters by client + branch)
            const [ledgerRes, histRes] = await Promise.all([
                fetch(`${CUSTOMER_API}/${customerId}/ledger`, { headers }).catch(() => null),
                fetch(`${CUSTOMER_API}/${customerId}/history`, { headers }).catch(() => null),
            ]);
            const ledgerData = ledgerRes ? await ledgerRes.json().catch(() => ({})) : {};
            setLedger(ledgerData.success && Array.isArray(ledgerData.data) ? ledgerData.data : []);

            const histData = histRes ? await histRes.json().catch(() => ({})) : {};
            setServiceHistory(histData.success && Array.isArray(histData.data?.rows) ? histData.data.rows : []);
            setInsights(histData.success && histData.data?.insights ? { ...EMPTY_INSIGHTS, ...histData.data.insights } : EMPTY_INSIGHTS);
        } catch (error) {
            console.error('Failed to load customer profile:', error);
        } finally {
            setIsLoading(false);
        }
    }, [customerId, storeId, jwtToken, headers]);

    useEffect(() => { fetchCustomerData(); }, [fetchCustomerData]);

    // profile changes: loaded when the tab is opened
    useEffect(() => {
        if (activeTab !== 'changes' || changes !== null) return;
        fetch(`${CUSTOMER_API}/${customerId}/changes`, { headers })
            .then((r) => r.json())
            .then((j) => setChanges(j.success ? j.data : []))
            .catch(() => setChanges([]));
    }, [activeTab, changes, customerId, headers]);

    const flash = (text) => { setToast(text); setTimeout(() => setToast(''), 3500); };

    /** After a save: show the new values at once */
    const onSaved = (updated, message) => {
        if (updated) setCustomer((c) => ({ ...c, ...updated, family: updated.family || c.family || [], activePrepaidCards: c.activePrepaidCards }));
        setEditFocus(null);
        setChanges(null);            // the history reloads next time its tab opens
        flash(message || 'Saved.');
    };

    /** Quick remove of one family member (from the overview) */
    const removeFamily = async (index) => {
        if (!window.confirm('Remove this family member?')) return;
        setSaving(true);
        setFormError('');
        try {
            const family = (customer.family || []).filter((_, i) => i !== index);
            const res = await fetch(`${CUSTOMER_API}/${customerId}`, { method: 'PUT', headers, body: JSON.stringify({ family, expectedUpdatedAt: customer.updatedAt }) });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || json.success === false) throw new Error(json.message || `Could not save (error ${res.status}).`);
            onSaved(json.data, json.message);
        } catch (error) {
            setFormError(error.message === 'Failed to fetch' ? 'Cannot reach the server.' : error.message);
        } finally {
            setSaving(false);
        }
    };

    if (isLoading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Loader2 className={styles.spin} size={30} color="var(--h-color-primary-600)" /></div>;
    }
    if (!customer) {
        return <div style={{ textAlign: 'center', marginTop: '50px', color: 'var(--h-color-neutral-500)', fontFamily: 'Nunito', fontWeight: 700 }}>Customer Profile not found.</div>;
    }

    const { activePrepaidCards = [], activePackages = [], prepaidMemberships = [], discountMemberships = [], family = [] } = customer;
    const pendingCards = prepaidMemberships.filter((m) => m.status === 'PENDING_ALLOTMENT');
    const avgTicket = customer.totalVisits > 0 ? Math.round(customer.totalSpent / customer.totalVisits) : 0;
    const topStaff = insights.preferredStaff;
    const topService = insights.favouriteServices[0];

    const sortedPrepaid = [...activePrepaidCards].sort((a, b) => {
        const ai = isExpired(a.expiryDate) || a.currentBalance <= 0;
        const bi = isExpired(b.expiryDate) || b.currentBalance <= 0;
        return ai === bi ? 0 : ai ? 1 : -1;
    });
    const sortedPackages = [...activePackages].sort((a, b) => {
        const al = (a.services || []).reduce((acc, s) => acc + (s.totalQty - s.usedQty), 0);
        const bl = (b.services || []).reduce((acc, s) => acc + (s.totalQty - s.usedQty), 0);
        const ai = isExpired(a.expiryDate) || al <= 0;
        const bi = isExpired(b.expiryDate) || bl <= 0;
        return ai === bi ? 0 : ai ? 1 : -1;
    });
    const sortedVIPs = [...discountMemberships].sort((a, b) => {
        const ai = isExpired(a.expiryDate) || a.status !== 'ACTIVE';
        const bi = isExpired(b.expiryDate) || b.status !== 'ACTIVE';
        return ai === bi ? 0 : ai ? 1 : -1;
    });

    const tabs = [
        { id: 'overview', label: 'Overview & Analytics' },
        ...(canSeeHistory ? [{ id: 'history', label: 'Service History' }] : []),
        { id: 'passbook', label: 'Ledger / Passbook' },
        { id: 'memberships', label: 'Loyalty & Benefits' },
        ...(canEdit || canSeeHistory ? [{ id: 'changes', label: 'Profile changes' }] : []),
    ];

    const subNote = { fontSize: '10px', fontWeight: 700, color: 'var(--h-color-neutral-500)' };
    const chip = { padding: '1px 8px', borderRadius: 999, background: 'var(--h-color-primary-50, #f5f6ff)', color: 'var(--h-color-primary-700, #471ea7)', fontSize: 10.5, fontWeight: 800 };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button onClick={() => navigate('/clients')} className={styles.backBtn}><ArrowLeft size={14} /></button>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h1 className={styles.headerTitle}>{customer.name}</h1>
                            {customer.lastVisitDate && <span className={styles.lastVisitBadge}>Last Visit: {formatDate(customer.lastVisitDate)}</span>}
                        </div>
                        <span className={styles.headerSub}>
                            <Phone size={10} /> +91 {phoneText(customer.phone)}
                            {customer.lastEditedAt && <span style={{ marginLeft: 10, opacity: 0.8 }}>· Edited {formatDate(customer.lastEditedAt)}{customer.lastEditedByName ? ` by ${customer.lastEditedByName}` : ''}</span>}
                        </span>
                    </div>
                </div>
                {canEdit && (
                    <button className={styles.editBtn} onClick={() => { setFormError(''); setEditFocus(''); }}><Edit size={12} /> Edit Profile</button>
                )}
            </div>

            <div className={styles.scrollableContent}>
                {toast && (
                    <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px', padding: '10px 12px', borderRadius: 10, background: 'var(--h-color-success-100, #dcf8e3)', color: 'var(--h-color-success-600, #007a49)', fontSize: 12, fontWeight: 800 }}>
                        <CheckCircle2 size={14} /> {toast}
                    </div>
                )}

                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statLabel}><Wallet size={12} /> Khata Wallet</div>
                        <div className={`${styles.statVal} ${styles.valGreen}`}>₹{money(customer.walletBalance)}</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statLabel}><Activity size={12} /> Outstanding Due</div>
                        <div className={`${styles.statVal} ${styles.valRed}`}>₹{money(customer.dueAmount)}</div>
                    </div>
                    {canSeeSpend && (
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}><IndianRupee size={13} /> Lifetime Spent</div>
                            <div className={`${styles.statVal} ${styles.valDark}`}>₹{money(customer.totalSpent)}</div>
                        </div>
                    )}
                    <div className={styles.statCard}>
                        <div className={styles.statLabel}><Clock size={12} /> Total Visits</div>
                        <div className={`${styles.statVal} ${styles.valDark}`}>{customer.totalVisits || 0}</div>
                    </div>
                </div>

                <div className={styles.tabs}>
                    {tabs.map((tab) => (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabActive : styles.tabInactive}`}>{tab.label}</button>
                    ))}
                </div>

                <div className={styles.contentArea}>
                    {activeTab === 'overview' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                            <div>
                                <h3 className={styles.sectionTitle} style={{ marginBottom: '12px' }}><TrendingUp size={14} color="var(--h-color-primary-600)" /> Client Insights</h3>
                                <div className={styles.insightsGrid}>
                                    {canSeeSpend && (
                                        <div className={styles.insightCard}>
                                            <div><span className={styles.insightLabel}>Avg Ticket Size</span><div className={styles.insightVal}>₹{money(avgTicket)}</div></div>
                                            <IndianRupee size={16} color="var(--h-color-primary-300)" />
                                        </div>
                                    )}
                                    <div className={styles.insightCard} title={insights.staffBreakdown.map((s) => `${s.name}: ${plural(s.services, 'service', 'services')}`).join('\n')}>
                                        <div>
                                            <span className={styles.insightLabel}>Preferred Staff</span>
                                            <div className={styles.insightVal}>{topStaff?.name || 'N/A'}</div>
                                            {topStaff && <span style={subNote}>{plural(topStaff.services, 'service', 'services')} given</span>}
                                        </div>
                                        <User size={16} color="var(--h-color-primary-300)" />
                                    </div>
                                    <div className={styles.insightCard}>
                                        <div>
                                            <span className={styles.insightLabel}>Favourite Service</span>
                                            <div className={styles.insightVal}>{topService?.name || 'N/A'}</div>
                                            {topService && <span style={subNote}>Taken {plural(topService.count, 'time', 'times')}</span>}
                                        </div>
                                        <Layers size={16} color="var(--h-color-primary-300)" />
                                    </div>
                                </div>

                                <h3 className={styles.sectionTitle}><User size={14} color="var(--h-color-primary-600)" /> Personal Details</h3>
                                <div className={styles.detailGrid}><span className={styles.detailLabel}>Full Name</span> <span className={styles.detailVal}>{customer.name}</span></div>
                                <div className={styles.detailGrid}><span className={styles.detailLabel}>Mobile</span> <span className={styles.detailVal}>+91 {phoneText(customer.phone)}</span></div>
                                <div className={styles.detailGrid}><span className={styles.detailLabel}>Email</span> <span className={styles.detailVal}>{customer.email || 'N/A'}</span></div>
                                <div className={styles.detailGrid}><span className={styles.detailLabel}>Gender</span> <span className={styles.detailVal}>{GENDER_LABEL[customer.gender] || 'N/A'}</span></div>
                                <div className={styles.detailGrid}><span className={styles.detailLabel}>Date of Birth</span> <span className={styles.detailVal}>{formatDate(customer.dob)}</span></div>
                                <div className={styles.detailGrid}><span className={styles.detailLabel}><Heart size={10} /> Anniversary</span> <span className={styles.detailVal}>{formatDate(customer.anniversary)}</span></div>
                                <div className={styles.detailGrid}>
                                    <span className={styles.detailLabel}><MessageCircle size={10} /> WhatsApp offers</span>
                                    <span className={styles.detailVal} style={{ color: customer.whatsappOffers === false ? 'var(--h-color-danger-600, #be1025)' : 'var(--h-color-success-600, #007a49)' }}>
                                        {customer.whatsappOffers === false ? (customer.whatsappStopSource === 'REPLY' ? 'Off (client replied STOP)' : 'Off') : 'On'}
                                    </span>
                                </div>
                                {(customer.tags || []).length > 0 && (
                                    <div className={styles.detailGrid}>
                                        <span className={styles.detailLabel}><Tag size={10} /> Tags</span>
                                        <span className={styles.detailVal} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
                                            {customer.tags.map((t) => <span key={t} style={chip}>{t}</span>)}
                                        </span>
                                    </div>
                                )}

                                <h3 className={styles.sectionTitle} style={{ marginTop: '20px' }}><MapPin size={14} color="var(--h-color-primary-600)" /> Location & Notes</h3>
                                <p style={{ color: 'var(--h-color-neutral-800)', lineHeight: '1.4', margin: '0 0 10px 0', fontSize: '11.5px', fontWeight: 700 }}>{customer.address || 'No address provided.'}</p>
                                <div className={styles.notesBox}><strong>Notes: </strong>{customer.notes || 'No notes added for this client.'}</div>
                            </div>

                            <div>
                                <div className={styles.sectionHeader}>
                                    <h3 className={styles.sectionTitle}><Users size={14} color="var(--h-color-primary-600)" /> Family Members</h3>
                                    {canEdit && (
                                        <button className={styles.editBtn} style={{ padding: '4px 8px', fontSize: '10px' }} onClick={() => { setFormError(''); setEditFocus('family'); }}>
                                            <Plus size={10} /> Add / edit family
                                        </button>
                                    )}
                                </div>
                                {family.length === 0 ? (
                                    <p style={{ fontSize: '11px', color: 'var(--h-color-neutral-500)', fontWeight: 700 }}>No family members linked to this profile.</p>
                                ) : (
                                    <div className={styles.familyGrid}>
                                        {family.map((member, i) => (
                                            <div key={member._id || `${member.name}-${i}`} className={styles.familyCard}>
                                                <div className={styles.familyInfo}>
                                                    <h4>{member.name}</h4>
                                                    <p>{member.relation}{member.phone ? ` | +91 ${phoneText(member.phone)}` : ''}</p>
                                                </div>
                                                {canEdit && (
                                                    <div className={styles.familyActions}>
                                                        <button className={`${styles.iconBtn} ${styles.delete}`} title="Remove" onClick={() => removeFamily(i)} disabled={saving}><Trash2 size={12} /></button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {formError && editFocus === null && <p style={{ color: 'var(--h-color-danger-600)', fontSize: 11.5, fontWeight: 800 }}>{formError}</p>}
                            </div>
                        </div>
                    )}

                    {activeTab === 'history' && canSeeHistory && (
                        <div>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}><Layers size={14} color="var(--h-color-primary-600)" /> Services & Products History</h3>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--h-color-neutral-500)' }}>
                                    {plural(insights.totalBills, 'bill', 'bills')}, {plural(insights.totalServices, 'service', 'services')}
                                </span>
                            </div>
                            <div className={styles.tableWrapper}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Date</th><th>Invoice No</th><th>Service / Item Name</th><th>Consulted By</th><th>Served By (Staff)</th>
                                            <th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'center' }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {serviceHistory.length === 0 ? (
                                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '30px', color: 'var(--h-color-neutral-400)' }}>No services taken yet.</td></tr>
                                        ) : serviceHistory.map((item) => (
                                            <tr key={item._id}>
                                                <td>{formatDate(item.date)}</td>
                                                <td style={{ fontWeight: 800, color: 'var(--h-color-primary-600)' }}>{item.invoiceNo}</td>
                                                <td style={{ color: 'var(--h-color-neutral-900)' }}>
                                                    {item.serviceName}{item.qty > 1 ? ` × ${item.qty}` : ''}
                                                    {item.fromPackage && (
                                                        <span title={`From ${item.fromPackage}`} style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 999, background: 'var(--h-color-success-100, #dcf8e3)', color: 'var(--h-color-success-600, #007a49)', fontSize: '9.5px', fontWeight: 800 }}>
                                                            Package
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ color: 'var(--h-color-neutral-500)', fontStyle: 'italic' }}>{item.consultedBy}</td>
                                                <td style={{ color: 'var(--h-color-neutral-700)' }}>{item.staff}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--h-color-neutral-900)' }}>₹{money(item.amount)}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span className={`${styles.statusTag} ${item.status === 'completed' ? styles.statusComplete : styles.statusPending}`}>{item.status}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'passbook' && (
                        <div>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}><FileText size={14} color="var(--h-color-primary-600)" /> Ledger Transactions</h3>
                            </div>
                            <div className={styles.tableWrapper}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr><th>Date</th><th>Transaction Type</th><th style={{ textAlign: 'right' }}>Credit (In)</th><th style={{ textAlign: 'right' }}>Debit (Out)</th><th>Mode</th><th>Details / Note</th></tr>
                                    </thead>
                                    <tbody>
                                        {ledger.length === 0 ? (
                                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: 'var(--h-color-neutral-400)' }}>No ledger history found.</td></tr>
                                        ) : ledger.map((entry) => (
                                            <tr key={entry._id}>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <div>{formatDate(entry.createdAt)}</div>
                                                    <small style={{ color: 'var(--h-color-neutral-500)', fontSize: '9px' }}>{formatTime(entry.createdAt)}</small>
                                                </td>
                                                <td><span className={styles.tagType}>{String(entry.transactionType || '').replace(/_/g, ' ')}</span></td>
                                                <td style={{ textAlign: 'right' }} className={entry.credit > 0 ? styles.txtGreen : styles.txtMuted}>{entry.credit > 0 ? `+ ₹${money(entry.credit)}` : '-'}</td>
                                                <td style={{ textAlign: 'right' }} className={entry.debit > 0 ? styles.txtRed : styles.txtMuted}>{entry.debit > 0 ? `- ₹${money(entry.debit)}` : '-'}</td>
                                                <td>{entry.paymentMode}</td>
                                                <td style={{ color: 'var(--h-color-neutral-600)', maxWidth: '280px', whiteSpace: 'normal', fontSize: '10.5px' }}>{entry.note}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'memberships' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                            <section>
                                <h3 className={styles.sectionTitle}><Crown size={14} color="var(--h-color-primary-600)" /> VIP Discount Passes</h3>
                                {sortedVIPs.length === 0 ? (
                                    <p style={{ color: 'var(--h-color-neutral-500)', fontSize: '11.5px', fontWeight: 700 }}>No VIP passes purchased.</p>
                                ) : (
                                    <div className={styles.cardsGrid}>
                                        {sortedVIPs.map((vip) => {
                                            const isInactive = isExpired(vip.expiryDate) || vip.status !== 'ACTIVE';
                                            const daysLeft = getDaysLeft(vip.expiryDate);
                                            const isExpiringSoon = !isInactive && daysLeft <= 15;
                                            return (
                                                <div key={vip._id || vip.templateId} className={`${styles.discountCard} ${isInactive ? styles.inactiveCard : ''}`}>
                                                    <div className={styles.vipHeader}>
                                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                            <Crown size={16} color="#fde68a" />
                                                            <span className={styles.vipName} style={{ margin: 0 }}>{vip.name}</span>
                                                        </div>
                                                        {isInactive ? <span className={styles.inactiveBadge}>Expired</span> : (isExpiringSoon && <span className={styles.warnBadgeDark}>Exp in {daysLeft} Days</span>)}
                                                    </div>
                                                    <div className={styles.vipExp}>Valid Till: {formatDate(vip.expiryDate)}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>

                            <section>
                                <h3 className={styles.sectionTitle}><CreditCard size={14} color="var(--h-color-primary-600)" /> Prepaid Cards</h3>
                                {sortedPrepaid.length === 0 && pendingCards.length === 0 ? (
                                    <p style={{ color: 'var(--h-color-neutral-500)', fontSize: '11.5px', fontWeight: 700 }}>No prepaid cards purchased.</p>
                                ) : (
                                    <div className={styles.cardsGrid}>
                                        {sortedPrepaid.map((card) => {
                                            const isExhausted = card.currentBalance <= 0;
                                            const isInactive = isExpired(card.expiryDate) || isExhausted;
                                            const isLowBal = !isInactive && card.currentBalance <= 500;
                                            const daysLeft = getDaysLeft(card.expiryDate);
                                            const isExpiringSoon = !isInactive && daysLeft <= 15;
                                            return (
                                                <div key={card._id} className={`${styles.prepaidCard} ${isInactive ? styles.inactiveCard : ''}`}>
                                                    <div className={styles.cardTopLabel}>
                                                        Membership Card
                                                        {isInactive && <span className={styles.inactiveBadge}>{isExhausted ? 'Exhausted' : 'Expired'}</span>}
                                                        {isLowBal && <span className={styles.alertBadgeDark}>Low Bal</span>}
                                                        {isExpiringSoon && !isLowBal && <span className={styles.warnBadgeDark}>{daysLeft} Days Left</span>}
                                                    </div>
                                                    <div className={styles.cardNumber}>{String(card.cardNumber).replace(/(.{4})/g, '$1 ').trim()}</div>
                                                    <div className={styles.cardFlex}>
                                                        <div>
                                                            <div className={styles.cardBalLabel}>Balance</div>
                                                            <div className={`${styles.cardBalVal} ${isLowBal ? styles.lowBalance : ''}`}>₹{money(card.currentBalance)}</div>
                                                        </div>
                                                        <div className={styles.cardExp}>Valid Till: <br /><strong style={{ color: '#fff' }}>{formatDate(card.expiryDate)}</strong></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {pendingCards.map((mem) => (
                                            <div key={mem._id} className={styles.pendingCard}>
                                                <Gift size={20} color="var(--h-color-warning-500)" style={{ marginBottom: '8px' }} />
                                                <strong style={{ color: 'var(--h-color-neutral-900)', display: 'block', marginBottom: '4px', fontSize: '12px' }}>{mem.name}</strong>
                                                <span style={{ fontSize: '10px', color: 'var(--h-color-neutral-500)', fontWeight: 700 }}>Purchased on {formatDate(mem.purchaseDate)}</span>
                                                {hasPermission('memberships', 'allot_cards') && <button onClick={() => navigate('/card-allotment')} className={styles.primaryLinkBtn}>Allot Card Now</button>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>

                            <section>
                                <h3 className={styles.sectionTitle}><Package size={14} color="var(--h-color-primary-600)" /> Active Packages</h3>
                                {sortedPackages.length === 0 ? (
                                    <p style={{ color: 'var(--h-color-neutral-500)', fontSize: '11.5px', fontWeight: 700 }}>No service packages purchased.</p>
                                ) : (
                                    <div className={styles.cardsGrid}>
                                        {sortedPackages.map((pkg) => {
                                            const totalLeft = (pkg.services || []).reduce((acc, s) => acc + (s.totalQty - s.usedQty), 0);
                                            const isExhausted = totalLeft <= 0;
                                            const isInactive = isExpired(pkg.expiryDate) || isExhausted;
                                            const daysLeft = getDaysLeft(pkg.expiryDate);
                                            const isExpiringSoon = !isInactive && daysLeft <= 15;
                                            return (
                                                <div key={pkg._id} className={`${styles.packageCard} ${isInactive ? styles.inactiveCard : ''}`}>
                                                    <div className={styles.pkgHeader}>
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <strong style={{ color: 'var(--h-color-neutral-900)', fontSize: '12px' }}>{pkg.packageName}</strong>
                                                            {isInactive && <span className={styles.inactiveBadge}>{isExhausted ? 'Exhausted' : 'Expired'}</span>}
                                                            {isExpiringSoon && <span className={styles.warnBadgeLight}>{daysLeft} Days Left</span>}
                                                        </div>
                                                        <span style={{ fontSize: '9px', color: 'var(--h-color-neutral-500)', fontWeight: 800 }}>Exp: {formatDate(pkg.expiryDate)}</span>
                                                    </div>
                                                    <div className={styles.pkgBody}>
                                                        {(pkg.services || []).map((srv) => {
                                                            const left = srv.totalQty - srv.usedQty;
                                                            return (
                                                                <div key={srv._id || srv.itemName} className={styles.srvRow}>
                                                                    <span style={{ color: 'var(--h-color-neutral-600)' }}>{srv.itemName}</span>
                                                                    <strong style={{ color: left <= 0 ? 'var(--h-color-danger-600)' : 'var(--h-color-neutral-900)' }}>{left} Left</strong>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>
                        </div>
                    )}

                    {activeTab === 'changes' && (
                        <div>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}><History size={14} color="var(--h-color-primary-600)" /> Profile changes</h3>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--h-color-neutral-500)' }}>Who changed what, and when</span>
                            </div>
                            {changes === null ? (
                                <div style={{ padding: 24, textAlign: 'center' }}><Loader2 className={styles.spin} size={18} color="var(--h-color-primary-600)" /></div>
                            ) : changes.length === 0 ? (
                                <p style={{ color: 'var(--h-color-neutral-500)', fontSize: '11.5px', fontWeight: 700 }}>No changes yet.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {changes.map((row) => (
                                        <div key={row.id} style={{ padding: '12px 14px', border: '1px solid var(--h-color-neutral-150, #e9e9ec)', borderRadius: 12, background: '#fff' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8, fontSize: 11, fontWeight: 800, color: 'var(--h-color-neutral-500)' }}>
                                                <span>{row.byName || 'Someone'}{row.byRole ? ` (${String(row.byRole).toLowerCase()})` : ''}</span>
                                                <span>{formatDate(row.at)}, {formatTime(row.at)}</span>
                                            </div>
                                            {row.changes.map((c) => {
                                                const isPhone = c.field === 'phone';
                                                const from = isPhone && c.from ? phoneText(c.from) : c.from;
                                                const to = isPhone && c.to ? phoneText(c.to) : c.to;
                                                return (
                                                    <div key={c.field} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, padding: '4px 0', fontSize: 11.5 }}>
                                                        <strong style={{ color: 'var(--h-color-neutral-900)' }}>{c.label}</strong>
                                                        <span style={{ color: 'var(--h-color-neutral-700)', wordBreak: 'break-word' }}>
                                                            <span style={{ textDecoration: 'line-through', color: 'var(--h-color-neutral-400)' }}>{from || 'Empty'}</span>
                                                            {'  →  '}
                                                            <b>{to || 'Empty'}</b>
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {editFocus !== null && (
                <EditClientDrawer
                    customer={customer}
                    api={CUSTOMER_API}
                    headers={headers}
                    canEditPhone={canEditPhone}
                    canSeePhone={canSeePhone}
                    focus={editFocus}
                    onClose={() => setEditFocus(null)}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
};

export default ClientProfile;